import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Users, ScrollText, CheckCircle2, Info, Sparkles, Rocket, ArrowRight, Lock, Award, ShieldCheck, Target, TrendingUp, Brain, Briefcase, Camera, Layers, ChevronRight, Trophy, Star, GraduationCap, UserCircle, Gift, PartyPopper, Heart, Zap, Globe, PlayCircle, Film, Music, Building2, Mail, Tv, Banana, Code, Cpu, MapPin, Wifi, HandHeart, UserPlus, Percent, RefreshCw, DollarSign, Coins, Gem, Wine, Crown, Mic, Monitor, Calendar, Clock, BookOpen, Lightbulb, Compass } from 'lucide-react';
import { Navbar } from '../components/Navbar';
// Footer import removed — landing page uses a minimal inline footer to keep focus on the application
import { ScrollProgress } from '../components/ScrollProgress';
import { LanguageProvider } from '../context/LanguageContext';
// ---------- Primitives ----------
const Eyebrow = ({
  children
}: {
  children: React.ReactNode;
}) => <p className="text-amber-400/70 tracking-[0.3em] text-[10px] sm:text-xs uppercase font-light" data-id="element-3340" data-mp="3351b2zzbvqjf">
    {children}
  </p>;
const SectionHeading = ({
  eyebrow,
  title,
  highlight,
  intro
}: {
  eyebrow: string;
  title: string;
  highlight?: string;
  intro?: string;
}) => <div className="max-w-3xl mx-auto text-center" data-id="element-3341" data-mp="3hxl8djzsr3sy">
    <Eyebrow data-id="element-3342" data-mp="3mvd993if609a">{eyebrow}</Eyebrow>
    <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-white mt-5 mb-6 leading-[1.05] tracking-tight" data-id="element-3343" data-mp="3mu1ygjqk3s8o">
      {title}
      {highlight && <>
          {' '}
          <span className="text-amber-400" data-id="element-3344" data-mp="3vvi5qim2re8o">{highlight}</span>
        </>}
    </h2>
    <div className="w-16 h-px bg-amber-400/30 mb-6 mx-auto" data-id="element-3345" data-mp="4q0anl878yyf" />
    {intro && <p className="text-base sm:text-lg text-white/65 font-light leading-relaxed max-w-2xl mx-auto" data-id="element-3346" data-mp="21hqooy1f1s37">
        {intro}
      </p>}
  </div>;
const PremiumCard = ({
  icon: Icon,
  title,
  body,
  highlight = false
}: {
  icon: React.ElementType;
  title: string;
  body: string;
  highlight?: boolean;
}) => <div className={`group relative border ${highlight ? 'border-amber-400/40' : 'border-amber-400/15'} bg-gradient-to-b from-amber-950/10 to-transparent hover:border-amber-400/50 transition-colors duration-500 p-6 sm:p-7 h-full`} data-id="element-3347" data-mp="1uetwnxz7rl12">
    <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent" data-id="element-3348" data-mp="23eh00pbi70wr" />
    <div className="w-10 h-10 border border-amber-400/40 bg-black/40 flex items-center justify-center mb-5" data-id="element-3349" data-mp="2hd6tzw310kgw">
      <Icon className="w-4 h-4 text-amber-400" data-id="element-3350" data-mp="2dbbumx4noh5r" />
    </div>
    <h3 className="font-serif text-lg sm:text-xl text-white mb-3 leading-tight" data-id="element-3351" data-mp="2r3rvpsdzy541">
      {title}
    </h3>
    <p className="text-sm sm:text-[15px] text-white/60 leading-relaxed font-light" data-id="element-3352" data-mp="2m71e30mjn1gi">
      {body}
    </p>
  </div>;
// ====================================== TRAILER INTRO (above hero)
const TrailerIntro = () => <section id="trailer-intro" className="relative bg-black pt-24 pb-16 md:pt-28 md:pb-20 overflow-hidden" data-id="element-3353" data-mp="2w5h6r817yis4">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.08),_transparent_70%)] pointer-events-none" data-id="element-3354" data-mp="3fnlcpgr5fpkw" />
    <div className="container mx-auto px-4 relative z-10" data-id="element-3355" data-mp="35s1m99vctj3j">
      <div className="max-w-5xl mx-auto" data-id="element-3356" data-mp="35tmj56555e4k">
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7
      }} className="text-center mb-8" data-id="element-3357" data-mp="3aptp2yxjepmi">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-amber-400/40 bg-amber-400/10 mb-5" data-id="element-3358" data-mp="2n1k7k97cwo16">
            <PlayCircle className="w-3.5 h-3.5 text-amber-400" data-id="element-3359" data-mp="2s16kehj9w29g" />
            <span className="text-amber-400 tracking-[0.3em] text-[10px] uppercase font-bold" data-id="element-3360" data-mp="2d2uls5ccajfn">
              The World You&rsquo;d Be Building
            </span>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-white leading-[1.05] tracking-tight mb-4" data-id="element-3361" data-mp="2i41ty5ie9kmu">
            Canada&rsquo;s Most Ambitious{' '}
            <span className="text-amber-400 italic" data-id="element-3362" data-mp="3gf336az2m55o">
              Immersive Cultural Production
            </span>
          </h2>
          <p className="text-white/55 text-sm sm:text-base font-light max-w-2xl mx-auto" data-id="element-3363" data-mp="3bkyw7rpt1x4l">
            Before you apply, see what fellows contribute to — a live,
            large-scale Sikh Kingdoms experience being built across film,
            immersive, AI, and culture.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="relative border border-amber-400/30 bg-black p-2 shadow-[0_0_60px_rgba(212,175,55,0.2)]" data-id="element-3364" data-mp="2xpn29mjh4aoq">
          <span className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-3365" data-mp="lyj0ip5h3bpf" />
          <div className="relative aspect-video bg-black overflow-hidden" data-id="element-3366" data-mp="ygbyyrnvd2b2">
            <iframe className="absolute inset-0 w-full h-full" src="https://www.youtube.com/embed/2yMWvJinVgk?rel=0" title="Sikh Kingdoms — Official Experience Trailer" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-id="element-3367" data-mp="ye8yfdp3bqs3" />
          </div>
        </motion.div>

        <p className="text-center mt-5 text-white/40 text-xs tracking-wide font-light italic" data-id="element-3368" data-mp="kgu53xvpuyuu">
          Official Experience Trailer · Sikh Kingdoms
        </p>
      </div>
    </div>
  </section>;
// ====================================== HERO
const Hero = () => <section className="relative w-full bg-black flex items-center justify-center overflow-hidden py-20 md:py-24 border-t border-amber-400/10" data-id="element-3369" data-mp="1vxtx0se5jboc">
    <div className="absolute inset-0" data-id="element-3370" data-mp="s1kipkb2zv5">
      <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black" data-id="element-3371" data-mp="qzyxr5nyuz6" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(212,175,55,0.18),_transparent_55%)] pointer-events-none" data-id="element-3372" data-mp="3rycgw8edfcg5" />
    </div>
    <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 0.5
  }} transition={{
    duration: 2,
    delay: 0.5
  }} className="absolute top-32 left-[10%] hidden lg:block" data-id="element-3373" data-mp="5rxedvzahawz">
      <Sparkles className="w-6 h-6 text-amber-400/40" data-id="element-3374" data-mp="399o1u32kk1ca" />
    </motion.div>
    <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 0.4
  }} transition={{
    duration: 2,
    delay: 1
  }} className="absolute bottom-40 right-[8%] hidden lg:block" data-id="element-3375" data-mp="3n015vuccavwc">
      <Zap className="w-7 h-7 text-amber-400/40" data-id="element-3376" data-mp="2uhdr0wnxsd3p" />
    </motion.div>

    <div className="relative z-10 text-center px-4 max-w-5xl mx-auto" data-id="element-3377" data-mp="2qh3l1k059mou">
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.7
    }} className="inline-flex flex-wrap items-center justify-center gap-2 mb-7" data-id="element-3378" data-mp="2gdeph5atagw0">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-amber-400 bg-amber-400/10 shadow-[0_0_20px_rgba(212,175,55,0.2)]" data-id="element-3379" data-mp="2bebtsbgq1n0p">
          <GraduationCap className="w-3 h-3 text-amber-400" data-id="element-3380" data-mp="2z2d0deucatdh" />
          <span className="text-amber-400 tracking-[0.3em] text-[9px] sm:text-[10px] uppercase font-bold" data-id="element-3381" data-mp="342hjzdqjo47x">
            High School &amp; University
          </span>
        </span>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-amber-400 bg-amber-400/10 shadow-[0_0_20px_rgba(212,175,55,0.2)]" data-id="element-3382" data-mp="2u4tlccptickh">
          <Calendar className="w-3 h-3 text-amber-400" data-id="element-3383" data-mp="1dps1s8vw4s3w" />
          <span className="text-amber-400 tracking-[0.3em] text-[9px] sm:text-[10px] uppercase font-bold" data-id="element-3384" data-mp="1iqg1rk23kja9">
            Just 3 Hours / Week
          </span>
        </span>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-amber-400 bg-amber-400/10 shadow-[0_0_20px_rgba(212,175,55,0.2)]" data-id="element-3385" data-mp="v0k6krc444fw">
          <Gift className="w-3 h-3 text-amber-400" data-id="element-3386" data-mp="q28zgod7c81d" />
          <span className="text-amber-400 tracking-[0.3em] text-[9px] sm:text-[10px] uppercase font-bold" data-id="element-3387" data-mp="m05hdgw12574">
            Free for Selected Students
          </span>
        </span>
      </motion.div>

      <motion.h1 initial={{
      opacity: 0,
      y: 24
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.9,
      delay: 0.1
    }} className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white leading-[1.02] tracking-tight mb-4" data-id="element-3388" data-mp="1drbncdyfix4b">
        <span className="block text-amber-400/80 font-serif italic text-base sm:text-lg md:text-xl tracking-[0.3em] uppercase not-italic mb-4 sm:mb-5" data-id="element-3389" data-mp="21hxk392zguz0">
          Summer Fellowship
        </span>
        Entrepreneurship and
        <br className="hidden sm:block" data-id="element-3390" data-mp="3kbqy134atkzs" />{' '}
        <span className="text-amber-400" data-id="element-3391" data-mp="3kcscziofqrms">AI Opportunity</span>
      </motion.h1>

      <motion.p initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.9,
      delay: 0.3
    }} className="font-serif italic text-base sm:text-lg md:text-xl text-amber-400/90 mb-3 max-w-3xl mx-auto" data-id="element-3392" data-mp="3od2rjjqamqd9">
        Canada&rsquo;s Premier Punjabi Entrepreneurship and AI Discovery
        Fellowship
      </motion.p>

      <motion.p initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.9,
      delay: 0.4
    }} className="flex flex-col items-center gap-2 mb-7" data-id="element-3393" data-mp="bwh0m8bct3yc">
        <p className="text-amber-400/90 text-xs tracking-[0.3em] uppercase font-bold" data-id="element-3394" data-mp="pv6ulf2vmnih">
          Presented by Akal Steel
        </p>
        <p className="text-white/40 text-[10px] tracking-[0.3em] uppercase font-light" data-id="element-3395" data-mp="1lyhaea5iaio8">
          In partnership with the Sikh Foundation of Canada
        </p>
      </motion.p>

      <div className="w-24 h-px bg-amber-400/50 mx-auto mb-7" data-id="element-3396" data-mp="1uwjj7bvhefm2" />

      <motion.p initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.8,
      delay: 0.5
    }} className="text-base sm:text-lg md:text-xl text-white/70 font-light leading-relaxed max-w-3xl mx-auto mb-5" data-id="element-3397" data-mp="1zob0evsqz8zj">
        Build real things alongside{' '}
        <span className="text-amber-400 font-medium" data-id="element-3398" data-mp="2in6qpw1hj383">
          Emmy &amp; Oscar-winning creators
        </span>
        , learn entrepreneurship from professors at the world&rsquo;s top
        business schools — including{' '}
        <span className="text-amber-400 font-medium" data-id="element-3399" data-mp="o8e0ydna96o2">Ivey</span>{' '}
        <span className="text-white/55 text-sm" data-id="element-3400" data-mp="gpmuu0zuwmgf">(Canada&rsquo;s #1)</span> and{' '}
        <span className="text-amber-400 font-medium" data-id="element-3401" data-mp="grhgjhiycilk">INSEAD</span>{' '}
        <span className="text-white/55 text-sm" data-id="element-3402" data-mp="bxd96x5ry8jy">(Europe&rsquo;s #1)</span> — and
        earn a letter of recommendation that actually matters.
      </motion.p>

      <motion.p initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.8,
      delay: 0.6
    }} className="text-white/55 text-sm sm:text-base font-light italic max-w-2xl mx-auto mb-4" data-id="element-3403" data-mp="2yrtd9rot7mv">
        For high school and university students. Apply solo — or with a friend.
      </motion.p>

      <motion.p initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.8,
      delay: 0.65
    }} className="text-white/40 text-xs sm:text-sm font-light max-w-2xl mx-auto mb-10" data-id="element-3404" data-mp="lp982afoq2p8">
        <span className="text-amber-400/80" data-id="element-3405" data-mp="37vm7nbd1jgvb">Parents:</span> this is a
        structured, mentor-led summer program.{' '}
        <span className="text-white/55" data-id="element-3406" data-mp="3cu5tlbrmwsno">Just 3 hours a week.</span> Designed for
        ambitious students. Free for those selected.
      </motion.p>

      <motion.div initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.7,
      delay: 0.75
    }} className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8" data-id="element-3407" data-mp="2yxseg9akp3cd">
        <a href="#apply" className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-amber-400 text-black text-xs tracking-[0.3em] uppercase font-bold hover:bg-amber-300 transition-colors shadow-[0_0_50px_rgba(212,175,55,0.4)]" data-id="element-3408" data-mp="2f7ymnv7uyo85">
          Apply Now
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" data-id="element-3409" data-mp="2b6d2rpwwp9og" />
        </a>
        <a href="#at-a-glance" className="inline-flex items-center justify-center gap-2 px-6 py-4 border border-amber-400/40 hover:border-amber-400 text-amber-400 text-xs tracking-[0.3em] uppercase font-semibold hover:bg-amber-400/5 transition-colors" data-id="element-3410" data-mp="1rj9x6bkm5wmo">
          <Info className="w-4 h-4" data-id="element-3411" data-mp="1wjee22pfotdf" />
          See the Details
        </a>
      </motion.div>

      <motion.p initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.7,
      delay: 1
    }} className="text-amber-400 font-bold text-xs tracking-[0.4em] uppercase" data-id="element-3412" data-mp="2aixjuourvsmk">
        Earn Your Place
      </motion.p>
    </div>
  </section>;
// ====================================== CNA FEATURED VIDEO
const CNAFeatured = () => <section id="featured" className="relative py-20 md:py-24 bg-black border-t border-amber-400/10 scroll-mt-20" data-id="element-3413" data-mp="2tagm2tgz32nj">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.07),_transparent_70%)] pointer-events-none" data-id="element-3414" data-mp="2oc5dsn6bt07f" />
    <div className="container mx-auto px-4 relative z-10" data-id="element-3415" data-mp="rjzs6hjyjj31">
      <div className="max-w-4xl mx-auto" data-id="element-3416" data-mp="riexmrxn8056">
        <div className="text-center mb-10" data-id="element-3417" data-mp="wag39v0b2sm8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-amber-400/40 bg-amber-400/10 mb-5" data-id="element-3418" data-mp="wbhqsyyid7ku">
            <Tv className="w-3.5 h-3.5 text-amber-400" data-id="element-3419" data-mp="10enrzs6rk8kg" />
            <span className="text-amber-400 tracking-[0.3em] text-[10px] uppercase font-bold" data-id="element-3420" data-mp="3nc4w8vpxcjf">
              Featured by Channel News Asia
            </span>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-white mb-3 leading-tight" data-id="element-3421" data-mp="cn8qu2qq3xas">
            Professor Ash Singh on{' '}
            <span className="text-amber-400 italic" data-id="element-3422" data-mp="hnmyvksbr78t">Millionaire Minds</span>
          </h2>
          <p className="text-white/55 text-sm sm:text-base font-light max-w-xl mx-auto" data-id="element-3423" data-mp="1o1cfctejkt8g">
            Featured globally on CNA&rsquo;s Millionaire Minds — the
            entrepreneurship faculty leading this fellowship.
          </p>
        </div>
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="relative border border-amber-400/30 bg-black p-2 shadow-[0_0_60px_rgba(212,175,55,0.2)]" data-id="element-3424" data-mp="1y62n5fwmmrkl">
          <span className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-3425" data-mp="1kfh51tezylnr" />
          <div className="relative aspect-video bg-black overflow-hidden" data-id="element-3426" data-mp="1fhxogmuttwsn">
            <iframe className="absolute inset-0 w-full h-full" src="https://www.youtube.com/embed/2c0eBgOG2yU?rel=0" title="Professor Ash Singh — Channel News Asia: Millionaire Minds" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-id="element-3427" data-mp="1ah0546y2ywpk" />
          </div>
        </motion.div>
        <p className="text-center mt-5 text-white/40 text-xs tracking-wide font-light italic" data-id="element-3428" data-mp="12wpbbimo4z64">
          Channel News Asia · Millionaire Minds
        </p>
      </div>
    </div>
  </section>;
// ====================================== AT A GLANCE (parent-friendly quick facts)
const AtAGlance = () => {
  const facts = [{
    icon: GraduationCap,
    label: 'Who',
    value: 'High school & university students'
  }, {
    icon: Clock,
    label: 'Time',
    value: 'Just 3 hours / week + optional extra work'
  }, {
    icon: Calendar,
    label: 'When',
    value: 'Summer 2026 cohort'
  }, {
    icon: MapPin,
    label: 'Where',
    value: 'Toronto (Distillery District) or Virtual'
  }, {
    icon: DollarSign,
    label: 'Cost',
    value: 'Free · Refundable $250 commitment'
  }, {
    icon: Mail,
    label: 'You Earn',
    value: 'Recommendation from Ivey & INSEAD faculty'
  }];
  return <section id="at-a-glance" className="relative py-20 md:py-24 bg-[#080808] border-t border-amber-400/10 scroll-mt-20" data-id="element-3429" data-mp="3p67zlxoqrd7e">
      <div className="container mx-auto px-4" data-id="element-3430" data-mp="2xf4rk6d6dkd">
        <div className="max-w-3xl mx-auto text-center mb-10" data-id="element-3431" data-mp="2v2m14ss7z69">
          <Eyebrow data-id="element-3432" data-mp="3u4hqvx7chtnq">For Students and Parents</Eyebrow>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-white mt-5 mb-5 leading-tight" data-id="element-3433" data-mp="36dvmcfwjurih">
            The Opportunity, <span className="text-amber-400" data-id="element-3434" data-mp="31faup4vd5g5n">At a Glance</span>
          </h2>
          <div className="w-16 h-px bg-amber-400/30 mb-5 mx-auto" data-id="element-3435" data-mp="3g3oxxgwoz6mq" />
          <p className="text-white/60 text-sm sm:text-base font-light leading-relaxed" data-id="element-3436" data-mp="3g501pgq50edu">
            Everything parents and students need to know in one scan.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-w-5xl mx-auto" data-id="element-3437" data-mp="2nltd3pm8mdi9">
          {facts.map((f, i) => <motion.div key={f.label} initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: i * 0.05
        }} className="relative border border-amber-400/20 bg-gradient-to-br from-amber-950/12 to-transparent p-5 flex items-start gap-4" data-id="element-3438" data-mp="2imgpv3rgf4f5" data-mpi={i}>
              <span className="absolute -top-px left-5 right-5 h-px bg-gradient-to-r from-transparent via-amber-400/55 to-transparent" data-id="element-3439" data-mp="2l1467v190t7f" />
              <div className="w-10 h-10 border border-amber-400/40 bg-black/40 flex items-center justify-center shrink-0" data-id="element-3440" data-mp="2usqm44oj7ggi">
                <f.icon className="w-4 h-4 text-amber-400" data-id="element-3441" data-mp="2zsmjhl2q49n3" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-3442" data-mp="33t5bpg8j3hpt">
                <p className="text-amber-400 text-[10px] tracking-[0.3em] uppercase font-bold mb-1" data-id="element-3443" data-mp="33ty9hrpiewvt">
                  {f.label}
                </p>
                <p className="text-white text-sm font-light leading-snug" data-id="element-3444" data-mp="o4pzjnu2nhkj">
                  {f.value}
                </p>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
};
// ====================================== WHY THIS MATTERS (parents + students)
const WhyThisMatters = () => {
  const parentPoints = [{
    icon: Lightbulb,
    title: 'AI is the defining skill of this decade',
    body: 'Students who learn to build with AI now will lead the next generation of industries — careers, universities, and creative work are all reshaping around it.'
  }, {
    icon: BookOpen,
    title: 'A serious credential for university',
    body: 'A real letter of recommendation from Ivey (Canada\u2019s #1) and INSEAD (Europe\u2019s #1) faculty — earned through actual work, not attendance.'
  }, {
    icon: ShieldCheck,
    title: 'Safe, supervised, and structured',
    body: 'Mentor-led sessions, weekly Friday check-ins, individual MBA pairing. Your child is supported, not left to figure it out alone.'
  }, {
    icon: Clock,
    title: 'Only 3 hours a week',
    body: 'Designed to fit alongside school, summer plans, and family time. Optional extra work for students who want to go deeper.'
  }, {
    icon: DollarSign,
    title: 'Fully sponsored',
    body: 'The program itself is free for selected students. A refundable $250 commitment deposit is returned in full on completion.'
  }, {
    icon: Compass,
    title: 'Real mentorship, not a class',
    body: 'Emmy and Oscar-winning creators, top global business school faculty, MBA students — your child works alongside people who have actually built things.'
  }];
  const studentPoints = [{
    icon: Rocket,
    title: 'You build real things',
    body: 'Not theory. You join a Build Team and ship something across the summer — collectibles, audio, AI, content, product, or events.'
  }, {
    icon: Sparkles,
    title: 'Use the actual AI tools builders use',
    body: 'Hands-on with Claude Code, Claude Cowork, Nano Banana Pro and the latest agentic AI — the same stack used by startups today.'
  }, {
    icon: Trophy,
    title: 'Mentors who actually built it',
    body: 'Direct access to Emmy and Oscar-winning creators, producers, and faculty from Ivey and INSEAD. People you couldn\u2019t normally get a coffee with.'
  }, {
    icon: UserPlus,
    title: 'Apply with a friend',
    body: 'You can apply solo or with a friend. Some of the best work happens in pairs — and you\u2019ll meet other ambitious students like you.'
  }, {
    icon: Mail,
    title: 'A letter that actually opens doors',
    body: 'A recommendation from Professor Ash Singh based on the work you did — not a generic teacher reference. Universities notice.'
  }, {
    icon: Heart,
    title: 'You become part of something',
    body: 'You contribute to Canada\u2019s most ambitious immersive cultural production. You can point to it the rest of your life.'
  }];
  return <section className="relative py-24 md:py-32 bg-black border-t border-amber-400/10" data-id="element-3445" data-mp="71qmrub9hgxx">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(212,175,55,0.07),_transparent_60%)] pointer-events-none" data-id="element-3446" data-mp="29p8k2uj9g59" />
      <div className="container mx-auto px-4 relative z-10" data-id="element-3447" data-mp="fzkawhyvh4a0">
        <div className="max-w-3xl mx-auto text-center mb-14" data-id="element-3448" data-mp="byqk9gu1pbdf">
          <Eyebrow data-id="element-3449" data-mp="pzjf62o9e8h2">Why This Matters</Eyebrow>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-white mt-5 mb-5 leading-[1.05] tracking-tight" data-id="element-3450" data-mp="py8e4am77etn">
            For Parents. For <span className="text-amber-400" data-id="element-3451" data-mp="kynf607136gx">Students.</span>
          </h2>
          <div className="w-16 h-px bg-amber-400/30 mb-6 mx-auto" data-id="element-3452" data-mp="2rrg0g408awp1" />
          <p className="text-white/65 text-base sm:text-lg font-light leading-relaxed" data-id="element-3453" data-mp="2msmqglk5hntm">
            Two perspectives. One opportunity. Read what speaks to you.
          </p>
        </div>

        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-3454" data-mp="179ezhctje9ih">
          {/* FOR PARENTS */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="relative border border-amber-400/30 bg-gradient-to-b from-amber-950/15 via-black/40 to-black p-7 sm:p-8" data-id="element-3455" data-mp="sixzzz0gpto8">
            <span className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-3456" data-mp="9m6ls3zs24r1" />
            <div className="inline-flex items-center gap-2 px-2.5 py-1 border border-amber-400/45 bg-amber-400/10 mb-6" data-id="element-3457" data-mp="ekgfsb9rmzb2">
              <ShieldCheck className="w-3 h-3 text-amber-400" data-id="element-3458" data-mp="23xbggisvz6u8" />
              <span className="text-amber-400 text-[10px] tracking-[0.3em] uppercase font-bold" data-id="element-3459" data-mp="28xpj1lbpxokv">
                For Parents
              </span>
            </div>
            <h3 className="font-serif text-2xl sm:text-3xl text-white leading-tight mb-3" data-id="element-3460" data-mp="4tuvea4fd3qo">
              Why your child should apply
            </h3>
            <p className="text-white/60 text-sm font-light leading-relaxed mb-7" data-id="element-3461" data-mp="3vyut27jwiyrf">
              A serious, supervised summer opportunity that builds the skills
              and credentials universities are paying attention to right now.
            </p>
            <div className="space-y-4" data-id="element-3462" data-mp="3hbjdluklmum1">
              {parentPoints.map(p => <div key={p.title} className="flex items-start gap-3 pb-4 border-b border-amber-400/10 last:border-b-0 last:pb-0" data-id="element-3463" data-mp="3d9y48j79wygl">
                  <div className="w-8 h-8 border border-amber-400/35 bg-black/40 flex items-center justify-center shrink-0" data-id="element-3464" data-mp="33c0hy18z5bqm">
                    <p.icon className="w-3.5 h-3.5 text-amber-400" data-id="element-3465" data-mp="2ycnkynoe2oab" />
                  </div>
                  <div className="flex-1" data-id="element-3466" data-mp="2o9ice43kcx2d">
                    <p className="text-white text-sm font-medium leading-snug mb-1" data-id="element-3467" data-mp="2o8qq5jvuwbor">
                      {p.title}
                    </p>
                    <p className="text-white/55 text-xs sm:text-[13px] font-light leading-relaxed" data-id="element-3468" data-mp="2y7fyakxyahrt">
                      {p.body}
                    </p>
                  </div>
                </div>)}
            </div>
            <a href="#apply" className="mt-7 group inline-flex items-center justify-center gap-2 w-full px-5 py-3.5 border border-amber-400/50 hover:border-amber-400 hover:bg-amber-400/5 text-amber-400 text-[10px] tracking-[0.3em] uppercase font-bold transition-colors" data-id="element-3469" data-mp="25jk2dig7gtw3">
              Apply on Behalf of Your Child
              <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" data-id="element-3470" data-mp="1ms12vlle3xyt" />
            </a>
          </motion.div>

          {/* FOR STUDENTS */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="relative border border-amber-400/30 bg-gradient-to-b from-amber-950/15 via-black/40 to-black p-7 sm:p-8" data-id="element-3471" data-mp="1rp9ndsu0ge38">
            <span className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-3472" data-mp="10slmdym2tafm" />
            <div className="inline-flex items-center gap-2 px-2.5 py-1 border border-amber-400/45 bg-amber-400/10 mb-6" data-id="element-3473" data-mp="vuabdkk1otvt">
              <Rocket className="w-3 h-3 text-amber-400" data-id="element-3474" data-mp="1andmpa71mbe3" />
              <span className="text-amber-400 text-[10px] tracking-[0.3em] uppercase font-bold" data-id="element-3475" data-mp="15pu2ltnnz4e8">
                For Students
              </span>
            </div>
            <h3 className="font-serif text-2xl sm:text-3xl text-white leading-tight mb-3" data-id="element-3476" data-mp="2h6bt7d4kycsy">
              Why you should apply
            </h3>
            <p className="text-white/60 text-sm font-light leading-relaxed mb-7" data-id="element-3477" data-mp="2onqi1t5q8leg">
              This isn&rsquo;t a course. You get to build real things with real
              people who&rsquo;ve done it — and walk away with something
              you&rsquo;re actually proud of.
            </p>
            <div className="space-y-4" data-id="element-3478" data-mp="2jqqim13dugwb">
              {studentPoints.map(p => <div key={p.title} className="flex items-start gap-3 pb-4 border-b border-amber-400/10 last:border-b-0 last:pb-0" data-id="element-3479" data-mp="37h4418rgphhx">
                  <div className="w-8 h-8 border border-amber-400/35 bg-black/40 flex items-center justify-center shrink-0" data-id="element-3480" data-mp="2yoai4shifjv3">
                    <p.icon className="w-3.5 h-3.5 text-amber-400" data-id="element-3481" data-mp="32njfe7brdzve" />
                  </div>
                  <div className="flex-1" data-id="element-3482" data-mp="3qdvlrdrcvojv">
                    <p className="text-white text-sm font-medium leading-snug mb-1" data-id="element-3483" data-mp="3v5ditqq9d4zk">
                      {p.title}
                    </p>
                    <p className="text-white/55 text-xs sm:text-[13px] font-light leading-relaxed" data-id="element-3484" data-mp="36fsqncfx855a">
                      {p.body}
                    </p>
                  </div>
                </div>)}
            </div>
            <a href="#apply" className="mt-7 group inline-flex items-center justify-center gap-2 w-full px-5 py-3.5 bg-amber-400 text-black text-[10px] tracking-[0.3em] uppercase font-bold hover:bg-amber-300 transition-colors shadow-[0_0_30px_rgba(212,175,55,0.3)]" data-id="element-3485" data-mp="2wp7nrct7segk">
              Start Your Application
              <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" data-id="element-3486" data-mp="3fewaiimwwoku" />
            </a>
          </motion.div>
        </div>
      </div>
    </section>;
};
// ====================================== SELECTIVITY (with competitive %)
const SelectivityBlock = () => {
  const items = [{
    icon: Percent,
    title: 'Only a Small Percentage Selected',
    body: 'Due to limited capacity, only a small percentage of applicants are accepted. Apply thoughtfully.'
  }, {
    icon: Award,
    title: 'Limited Cohort',
    body: 'We invest deeply in those we accept. Spots are intentionally few.'
  }, {
    icon: ShieldCheck,
    title: 'Careful Review',
    body: 'Every application is reviewed for fit, commitment, and potential.'
  }];
  return <section className="relative py-20 md:py-24 bg-black border-t border-amber-400/10" data-id="element-3487" data-mp="y7vyk4o94lie">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.05),_transparent_70%)] pointer-events-none" data-id="element-3488" data-mp="1286ea91uiqag" />
      <div className="container mx-auto px-4 relative z-10" data-id="element-3489" data-mp="178lsf2tyc1th">
        <SectionHeading eyebrow="Competitive Selection" title="High Demand." highlight="Few Spots." intro="This is a serious application with a serious review process. Take it seriously and your effort will show." data-id="element-3490" data-mp="1c75fj6ka7jnf" />
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl mx-auto" data-id="element-3491" data-mp="3e03b3a5g0qc2">
          {items.map((it, i) => <motion.div key={it.title} initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.08
        }} data-id="element-3492" data-mp="34baf8fjgl4av" data-mpi={i}>
              <PremiumCard icon={it.icon} title={it.title} body={it.body} highlight data-id="element-3493" data-mp="3n94jkg9n6rmx" />
            </motion.div>)}
        </div>
      </div>
    </section>;
};
// ====================================== WHAT FELLOWS DO
const WhatYoullWorkOn = () => {
  const areas = [{
    icon: Brain,
    title: 'Artificial Intelligence',
    body: 'Build with generative AI — agents, automations, applied tools that ship.'
  }, {
    icon: Briefcase,
    title: 'Entrepreneurship',
    body: 'Product thinking, business models, customer discovery, real shipping discipline.'
  }, {
    icon: Camera,
    title: 'Digital Media',
    body: 'Storytelling, content production, brand strategy, audience growth.'
  }, {
    icon: Layers,
    title: 'Culture & Heritage',
    body: 'Sikh history, identity, and culture brought to life through modern formats.'
  }, {
    icon: PartyPopper,
    title: 'Live Events',
    body: 'Contribute to large-scale productions, festivals, and immersive activations.'
  }, {
    icon: Sparkles,
    title: 'Technology Stack',
    body: 'Web, mobile, immersive tech, collectibles, and the production pipelines behind them.'
  }];
  return <section className="relative py-24 md:py-32 bg-[#080808] border-t border-amber-400/10" data-id="element-3494" data-mp="3dnhifa76rqhv">
      <div className="container mx-auto px-4" data-id="element-3495" data-mp="39nqowyq9yw9n">
        <SectionHeading eyebrow="What You'll Actually Do" title="Six Disciplines." highlight="One Real Project." intro="Fellows pick an AI-related workstream and ship something across the summer. Not lectures — building." data-id="element-3496" data-mp="1cnw7jdntnk13" />
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl mx-auto" data-id="element-3497" data-mp="1rc952f2k5axp">
          {areas.map((a, i) => <motion.div key={a.title} initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.05
        }} data-id="element-3498" data-mp="tpjw1l9fty2c" data-mpi={i}>
              <PremiumCard icon={a.icon} title={a.title} body={a.body} data-id="element-3499" data-mp="2f1jzletbe93e" />
            </motion.div>)}
        </div>
      </div>
    </section>;
};
// ====================================== STUDENT BUILD TEAMS
const BuildTeams = () => {
  const teams = [{
    icon: Coins,
    tag: 'Systems & Mechanics',
    name: 'Token & XP System',
    tagline: 'Design how progression actually works.',
    bullets: ['Design the XP system and progression logic', 'Shape utility, unlocks, and redemption mechanics', 'Explore how tokens / rewards connect to behavior']
  }, {
    icon: Gem,
    tag: 'Physical × Digital',
    name: 'Coinage & Collectibles',
    tagline: 'Bring event coins and collectibles to life.',
    bullets: ['Design event coin concepts and storytelling', 'Shape physical collectible ideas', 'Connect physical and digital collectible logic']
  }, {
    icon: Wine,
    tag: 'Sensory & Story',
    name: 'Mocktails & Sensory',
    tagline: 'Build the taste and memory layer of the world.',
    bullets: ['Research pairings and historical drink references', 'Name and frame mocktails', 'Connect scent, drink, and memory sequences']
  }, {
    icon: Crown,
    tag: 'Digital Product',
    name: 'Lahore Treasury / Digital Collectibles',
    tagline: 'Help build the digital court.',
    bullets: ['Shape cards, sets, utility, and rewards', 'Design Misls, XP, and engagement logic', 'Test onboarding and growth ideas for younger audiences']
  }, {
    icon: Mic,
    tag: 'Audio & Narrative',
    name: 'Audio World / Podcast',
    tagline: 'Help shape the audio franchise.',
    bullets: ['Develop episode ideas, research, and character arcs', 'Support sound and story packaging', 'Think through social distribution for younger audiences']
  }, {
    icon: Monitor,
    tag: 'Web · Schools · Social',
    name: 'Website, School Pitch & Content',
    tagline: 'Make the public surface sharper.',
    bullets: ['Optimize landing pages, messaging, and user journeys', 'Frame school pitches and educator-friendly materials', 'Make short-form content and behind-the-scenes storytelling']
  }];
  return <section className="relative py-24 md:py-32 bg-black border-t border-amber-400/10" data-id="element-3500" data-mp="2ri2tqae9lv9y">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(212,175,55,0.07),_transparent_60%)] pointer-events-none" data-id="element-3501" data-mp="2mnfdeytf076g" />
      <div className="container mx-auto px-4 relative z-10" data-id="element-3502" data-mp="2wk1x3ka08s6w">
        <div className="max-w-3xl mx-auto text-center" data-id="element-3503" data-mp="2whfpxlr10ep7">
          <Eyebrow data-id="element-3504" data-mp="3a9vl60dor8fk">A Student Venture Studio Around Sikh Kingdoms</Eyebrow>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-white mt-5 mb-6 leading-[1.05] tracking-tight" data-id="element-3505" data-mp="35c3sh457viab">
            If Selected, You&rsquo;ll Join a{' '}
            <span className="text-amber-400" data-id="element-3506" data-mp="3k6qiwh24x4ss">Build Team</span>
          </h2>
          <div className="w-16 h-px bg-amber-400/30 mb-6 mx-auto" data-id="element-3507" data-mp="3fbkwzi8qfq4p" />
          <p className="text-base sm:text-lg text-white/65 font-light leading-relaxed max-w-2xl mx-auto" data-id="element-3508" data-mp="3mdzoval5iytn">
            Accepted fellows are placed onto a focused student team working
            across AI, culture, storytelling, digital products, events, and
            entrepreneurship. Placement is matched to your strengths and what
            the cohort needs.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl mx-auto" data-id="element-3509" data-mp="9wkx1etxph4i">
          {teams.map((t, i) => <motion.div key={t.name} initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.06
        }} className="group relative border border-amber-400/20 bg-gradient-to-b from-amber-950/12 via-black/40 to-black hover:border-amber-400/55 transition-colors p-6 sm:p-7 flex flex-col" data-id="element-3510" data-mp="dvld4pl125nt" data-mpi={i}>
              <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-amber-400/70 to-transparent" data-id="element-3511" data-mp="28dmqeir688p2" />
              <div className="flex items-start justify-between mb-5 gap-3" data-id="element-3512" data-mp="23fl8401kkden">
                <div className="w-11 h-11 border border-amber-400/45 bg-black/40 flex items-center justify-center shrink-0" data-id="element-3513" data-mp="3120spapalr6f">
                  <t.icon className="w-5 h-5 text-amber-400" data-id="element-3514" data-mp="2w48z8c901x04" />
                </div>
                <span className="text-[9px] tracking-[0.25em] uppercase text-amber-400/80 border border-amber-400/30 px-2 py-1 bg-amber-400/5 text-right" data-id="element-3515" data-mp="2r3bi82zq7907">
                  {t.tag}
                </span>
              </div>
              <p className="text-amber-400/70 text-[10px] tracking-[0.3em] uppercase font-bold mb-1" data-id="element-3516" data-mp="1fpquvhdc8mtv">
                Build Team
              </p>
              <h3 className="font-serif text-xl text-white leading-tight mb-2" data-id="element-3517" data-mp="20e06efms4bs3">
                {t.name}
              </h3>
              <p className="text-white/75 text-sm font-light italic mb-4" data-id="element-3518" data-mp="25dmn4z1tga5n">
                {t.tagline}
              </p>
              <ul className="space-y-2 pt-4 border-t border-amber-400/10 mt-auto" data-id="element-3519" data-mp="1ren7ui0717m5">
                {t.bullets.map(b => <li key={b} className="flex items-start gap-2.5 text-white/60 text-[13px] font-light leading-relaxed" data-id="element-3520" data-mp="1vf73ti91656p">
                    <ChevronRight className="w-3 h-3 text-amber-400/80 mt-1 shrink-0" data-id="element-3521" data-mp="2o29r1e89athn" />
                    <span data-id="element-3522" data-mp="2ab4skfgmk78w">{b}</span>
                  </li>)}
              </ul>
            </motion.div>)}
        </div>

        {/* Operating model */}
        <div className="mt-14 max-w-5xl mx-auto" data-id="element-3523" data-mp="322u5xzb1w55v">
          <div className="relative border border-amber-400/30 bg-gradient-to-br from-amber-950/15 via-black to-black p-7 sm:p-9" data-id="element-3524" data-mp="3222n7p2jy1x0">
            <span className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-3525" data-mp="vf3j3xrftrss" />
            <div className="flex items-center gap-2 mb-5 justify-center" data-id="element-3526" data-mp="qez4yee0595q">
              <Target className="w-3.5 h-3.5 text-amber-400" data-id="element-3527" data-mp="154n356qvzrp8" />
              <span className="text-amber-400 text-[10px] tracking-[0.3em] uppercase font-bold" data-id="element-3528" data-mp="156zkpisqg01r">
                How It Operates
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-3529" data-mp="ch0rdb7i9gra">
              {[{
              label: '1 Primary Team',
              sub: 'No floating across everything'
            }, {
              label: '1 Support Mentor',
              sub: 'Dedicated to your team'
            }, {
              label: '1 Specific Deliverable',
              sub: 'Shipped, not theoretical'
            }, {
              label: 'Friday Check-In',
              sub: 'Weekly cohort sync'
            }].map(m => <div key={m.label} className="border border-amber-400/15 bg-black/40 p-4 text-center" data-id="element-3530" data-mp="3eomlmkp7ynbw">
                  <p className="text-white font-serif text-sm sm:text-base leading-tight mb-1" data-id="element-3531" data-mp="3seq4r2ssy0x4">
                    {m.label}
                  </p>
                  <p className="text-white/45 text-[11px] font-light leading-snug" data-id="element-3532" data-mp="30ou0r60dj1bu">
                    {m.sub}
                  </p>
                </div>)}
            </div>
            <p className="text-center mt-6 text-white/55 text-sm font-light italic" data-id="element-3533" data-mp="2vs3koiokk5qf">
              Not just a fellowship — a{' '}
              <span className="text-amber-400 not-italic font-medium" data-id="element-3534" data-mp="34pxb5cznk55o">
                student venture studio
              </span>{' '}
              around Sikh Kingdoms.
            </p>
          </div>
        </div>
      </div>
    </section>;
};
// ====================================== AI TOOLS
const AITools = () => {
  const tools = [{
    icon: Banana,
    name: 'Nano Banana Pro',
    tag: 'Image & Vision',
    body: 'Cutting-edge image generation and visual workflows used by modern creative teams.'
  }, {
    icon: Code,
    name: 'Claude Code',
    tag: 'Engineering',
    body: 'Pair-program with Anthropic\u2019s coding agent. Build software end-to-end.'
  }, {
    icon: Cpu,
    name: 'Claude Cowork',
    tag: 'Multi-Agent Workflows',
    body: 'Collaborative AI workspaces where teams ship alongside intelligent agents.'
  }];
  return <section className="relative py-24 md:py-32 bg-black border-t border-amber-400/10" data-id="element-3535" data-mp="11ionptp4x0vh">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(212,175,55,0.08),_transparent_60%)] pointer-events-none" data-id="element-3536" data-mp="16ggcwfyea8vz" />
      <div className="container mx-auto px-4 relative z-10" data-id="element-3537" data-mp="1u4zmqzx3etq6">
        <SectionHeading eyebrow="The Stack You'll Use" title="Real AI Tools." highlight="Used by Builders Today." intro="Students will gain exposure to modern AI workflows and tools used by builders, startups, and innovators today." data-id="element-3538" data-mp="1yxsrvowh442s" />
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl mx-auto" data-id="element-3539" data-mp="1yv3gge7i3rro">
          {tools.map((t, i) => <motion.div key={t.name} initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.08
        }} className="relative border border-amber-400/30 bg-gradient-to-b from-amber-950/15 via-black to-black p-7 hover:border-amber-400/60 transition-colors" data-id="element-3540" data-mp="1uu9o98n0zqsz" data-mpi={i}>
              <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-amber-400/80 to-transparent" data-id="element-3541" data-mp="124koszpyryth" />
              <div className="flex items-center justify-between mb-5" data-id="element-3542" data-mp="3drwkc3ejtywh">
                <div className="w-12 h-12 border border-amber-400/50 bg-black/40 flex items-center justify-center" data-id="element-3543" data-mp="3iq7ukf8rabf8">
                  <t.icon className="w-5 h-5 text-amber-400" data-id="element-3544" data-mp="3mtdxpe0knmhh" />
                </div>
                <span className="text-[9px] tracking-[0.25em] uppercase text-amber-400/80 border border-amber-400/30 px-2 py-1 bg-amber-400/5" data-id="element-3545" data-mp="3rqw52zyxxtnd">
                  {t.tag}
                </span>
              </div>
              <h4 className="font-serif text-xl text-white mb-3 leading-tight" data-id="element-3546" data-mp="2z3tc0nd1ab4i">
                {t.name}
              </h4>
              <p className="text-white/55 text-sm font-light leading-relaxed" data-id="element-3547" data-mp="340a0jio5ari4">
                {t.body}
              </p>
            </motion.div>)}
        </div>
        <p className="text-center text-white/40 text-xs mt-8 italic font-light" data-id="element-3548" data-mp="2bcxlruo87nzd">
          + ongoing exposure to the latest agentic, generative, and applied AI
          tooling.
        </p>
      </div>
    </section>;
};
// ====================================== MENTORS / ASH SINGH
const ThePeople = () => <section className="relative py-24 md:py-32 bg-[#080808] border-t border-amber-400/10 overflow-hidden" data-id="element-3549" data-mp="hwjldhys3nbo">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(212,175,55,0.07),_transparent_60%)] pointer-events-none" data-id="element-3550" data-mp="vnoe003qbfci" />
    <div className="container mx-auto px-4 relative z-10" data-id="element-3551" data-mp="15kavcewujo9s">
      <SectionHeading eyebrow="The People You'll Work With" title="Mentors Who" highlight="Actually Built Things" intro="The people guiding you through the summer aren't theorists. They're operators, creators, and award-winning artists." data-id="element-3552" data-mp="15iq2ctq6kde1" />
      <div className="max-w-6xl mx-auto mt-14 space-y-5" data-id="element-3553" data-mp="1akyyfyycnvkd">
        {/* Award winners */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="relative border border-amber-400/30 bg-gradient-to-br from-amber-950/15 via-black to-black p-7 sm:p-9" data-id="element-3554" data-mp="3gdnifzlkf6ad">
          <span className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-3555" data-mp="2shiqh2xu70pl" />
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-7 items-start" data-id="element-3556" data-mp="2xfbxtzavm0rg">
            <div className="lg:col-span-2" data-id="element-3557" data-mp="2iil5ub3j7rbv">
              <div className="inline-flex items-center gap-2 px-2.5 py-1 border border-amber-400/40 bg-amber-400/10 mb-4" data-id="element-3558" data-mp="2ihrvz19a0lpi">
                <Trophy className="w-3 h-3 text-amber-400" data-id="element-3559" data-mp="2nftgzrq9iv3m" />
                <span className="text-amber-400 text-[10px] tracking-[0.3em] uppercase font-bold" data-id="element-3560" data-mp="29jpxotgxg75x">
                  Award-Winning Creators
                </span>
              </div>
              <h3 className="font-serif text-2xl sm:text-3xl text-white leading-tight mb-3" data-id="element-3561" data-mp="7s36aqt4ia8x">
                Emmy &amp; Oscar-winning mentors
              </h3>
              <p className="text-white/55 text-sm font-light leading-relaxed" data-id="element-3562" data-mp="abhnmd64bdbc">
                Direct access to the creators, producers, and artists shaping
                the immersive production.
              </p>
            </div>
            <div className="lg:col-span-3 grid grid-cols-2 gap-3" data-id="element-3563" data-mp="1a02ggduslfs">
              {[{
              icon: Trophy,
              label: 'Emmy Winners',
              sub: 'Television & narrative'
            }, {
              icon: Star,
              label: 'Oscar Winners',
              sub: 'Film & creative direction'
            }, {
              icon: Film,
              label: 'Producers',
              sub: 'Live & immersive'
            }, {
              icon: Music,
              label: 'Composers',
              sub: 'Score & sound design'
            }].map(c => <div key={c.label} className="border border-amber-400/20 bg-black/40 p-4 flex items-start gap-3" data-id="element-3564" data-mp="17xx71ki3ul1j">
                  <c.icon className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" data-id="element-3565" data-mp="1kaipgfit4hy8" />
                  <div data-id="element-3566" data-mp="1p811wfw19faz">
                    <p className="text-white text-sm font-medium leading-tight" data-id="element-3567" data-mp="1p7rb4q35z448">
                      {c.label}
                    </p>
                    <p className="text-white/45 text-[11px] mt-0.5 font-light" data-id="element-3568" data-mp="1u5kivnk4866m">
                      {c.sub}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </motion.div>

        {/* Ash Singh */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.1
      }} className="relative border-2 border-amber-400/50 bg-gradient-to-br from-amber-950/25 via-black to-black p-7 sm:p-9 shadow-[0_0_50px_rgba(212,175,55,0.15)]" data-id="element-3569" data-mp="27zk62q1smzel">
          <span className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-3570" data-mp="338y7g0tc1qs7" />
          <span className="absolute -top-3 left-8 text-[9px] tracking-[0.3em] uppercase text-black bg-amber-400 px-2 py-1 font-bold" data-id="element-3571" data-mp="3h7fqhl8amf3s">
            Featured Faculty
          </span>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-7 items-center" data-id="element-3572" data-mp="2ok1z02nd6br2">
            <div className="lg:col-span-2 flex flex-col items-start" data-id="element-3573" data-mp="2jkys0cntpvgg">
              <div className="relative w-24 h-24 rounded-full border-2 border-amber-400 bg-gradient-to-br from-amber-400/30 to-amber-950/30 flex items-center justify-center mb-5 shadow-[0_0_30px_rgba(212,175,55,0.3)]" data-id="element-3574" data-mp="2y93c9l2iyf1c">
                <span className="font-serif text-3xl text-amber-400" data-id="element-3575" data-mp="25s9422oqiggp">AS</span>
              </div>
              <p className="text-amber-400 text-[10px] tracking-[0.3em] uppercase font-bold mb-2" data-id="element-3576" data-mp="20u7mjm6yip7c">
                Monthly Sessions
              </p>
              <h3 className="font-serif text-2xl sm:text-3xl text-white leading-tight mb-2" data-id="element-3577" data-mp="gac8u3z5dg7s">
                Professor Ash Singh
              </h3>
              <p className="text-white/55 text-sm font-light leading-relaxed mb-4" data-id="element-3578" data-mp="7bigwqwrhe0k">
                One of the top adjunct professors of entrepreneurship globally.
              </p>
              <div className="inline-flex items-center gap-2 px-2.5 py-1 border border-amber-400/40 bg-amber-400/10" data-id="element-3579" data-mp="z2527zi4p3g1">
                <Tv className="w-3 h-3 text-amber-400" data-id="element-3580" data-mp="141reo6q4ufns" />
                <span className="text-amber-400 text-[9px] tracking-[0.25em] uppercase font-bold" data-id="element-3581" data-mp="13z58obiw487o">
                  Featured on CNA · Millionaire Minds
                </span>
              </div>
            </div>
            <div className="lg:col-span-3 space-y-3" data-id="element-3582" data-mp="v2e5zg9u1v44">
              <div className="border border-amber-400/25 bg-black/40 p-4" data-id="element-3583" data-mp="3aqwbhpsqw9o1">
                <div className="flex items-start gap-3" data-id="element-3584" data-mp="3frbouhd11h60">
                  <GraduationCap className="w-4 h-4 text-amber-400 mt-1 shrink-0" data-id="element-3585" data-mp="3fopigl1vh7pd" />
                  <div className="flex-1" data-id="element-3586" data-mp="2dtaekef9md5a">
                    <div className="flex items-center justify-between gap-2 mb-1" data-id="element-3587" data-mp="2isdbfbl3dd26">
                      <p className="text-amber-400/80 text-[10px] tracking-[0.25em] uppercase font-bold" data-id="element-3588" data-mp="2nrq62ii7fofb">
                        Ivey Business School
                      </p>
                      <span className="text-[9px] tracking-[0.2em] uppercase text-amber-400 border border-amber-400/40 px-1.5 py-0.5 bg-amber-400/10" data-id="element-3589" data-mp="2rr7cpf9darm2">
                        Canada&rsquo;s #1
                      </span>
                    </div>
                    <p className="text-white text-sm font-light leading-snug" data-id="element-3590" data-mp="2437exvdn20xz">
                      Adjunct Professor of Entrepreneurship
                    </p>
                  </div>
                </div>
              </div>
              <div className="border border-amber-400/25 bg-black/40 p-4" data-id="element-3591" data-mp="1cbrsc1c30e7r">
                <div className="flex items-start gap-3" data-id="element-3592" data-mp="1gdd0xahl26c5">
                  <Globe className="w-4 h-4 text-amber-400 mt-1 shrink-0" data-id="element-3593" data-mp="1gbs41e7sqbb4" />
                  <div className="flex-1" data-id="element-3594" data-mp="18u3spkktcdoo">
                    <div className="flex items-center justify-between gap-2 mb-1" data-id="element-3595" data-mp="13vshp6is7x4v">
                      <p className="text-amber-400/80 text-[10px] tracking-[0.25em] uppercase font-bold" data-id="element-3596" data-mp="13tpoyeq8vpwg">
                        INSEAD Business School
                      </p>
                      <span className="text-[9px] tracking-[0.2em] uppercase text-amber-400 border border-amber-400/40 px-1.5 py-0.5 bg-amber-400/10" data-id="element-3597" data-mp="uukr7amic0lc">
                        Europe&rsquo;s #1
                      </span>
                    </div>
                    <p className="text-white text-sm font-light leading-snug" data-id="element-3598" data-mp="g5gc42b0echg">
                      Adjunct Professor of Entrepreneurship · France
                    </p>
                  </div>
                </div>
              </div>
              <div className="border border-amber-400/45 bg-amber-400/10 p-4" data-id="element-3599" data-mp="74qjf7h1p27y">
                <div className="flex items-start gap-3" data-id="element-3600" data-mp="2788xcndqexu">
                  <Mail className="w-4 h-4 text-amber-400 mt-1 shrink-0" data-id="element-3601" data-mp="3onysqvov4dub" />
                  <div data-id="element-3602" data-mp="3omnsh5umlo7y">
                    <p className="text-amber-400 text-[10px] tracking-[0.25em] uppercase font-bold mb-1" data-id="element-3603" data-mp="3tn20wp05306i">
                      What You Earn
                    </p>
                    <p className="text-white text-sm font-light leading-snug" data-id="element-3604" data-mp="3erm1s5kpce39">
                      A{' '}
                      <span className="text-amber-400 font-medium" data-id="element-3605" data-mp="3jqyngmzu7f49">
                        letter of recommendation from Professor Ash Singh
                      </span>{' '}
                      based on the work you actually contribute through the
                      fellowship.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CNA Millionaire Minds video — embedded inside the Ash Singh card */}
          <div className="mt-8 pt-7 border-t border-amber-400/15" data-id="element-3606" data-mp="u6ltv2txmda6">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4" data-id="element-3607" data-mp="yzoqjju0a2ul">
              <div data-id="element-3608" data-mp="2join4vzyl3gg">
                <p className="text-amber-400 text-[10px] tracking-[0.3em] uppercase font-bold mb-1" data-id="element-3609" data-mp="2jo0ndpe3873s">
                  Featured by Channel News Asia
                </p>
                <p className="text-white/65 text-sm font-light italic" data-id="element-3610" data-mp="2telpvnwvtvrz">
                  Professor Ash Singh on{' '}
                  <span className="text-amber-400 not-italic" data-id="element-3611" data-mp="2ofioqek0w9pa">
                    Millionaire Minds
                  </span>
                </p>
              </div>
              <span className="text-white/35 text-[10px] tracking-wide font-light italic" data-id="element-3612" data-mp="1vr3ahu8ptvf0">
                Global broadcast feature
              </span>
            </div>
            <div className="relative aspect-video bg-black overflow-hidden border border-amber-400/25" data-id="element-3613" data-mp="29s7dkdrr6rpp">
              <iframe className="absolute inset-0 w-full h-full" src="https://www.youtube.com/embed/2c0eBgOG2yU?rel=0" title="Professor Ash Singh — Channel News Asia: Millionaire Minds" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-id="element-3614" data-mp="25rdo3fynx4up" />
            </div>
          </div>
        </motion.div>

        {/* MBA pairing */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.15
      }} className="relative border border-amber-400/30 bg-gradient-to-br from-amber-950/15 via-black to-black p-7 sm:p-9" data-id="element-3615" data-mp="pc9i4mvevmfb">
          <span className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-3616" data-mp="1d00s3jthbndf" />
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-7 items-start" data-id="element-3617" data-mp="1hzvilm9ceaxy">
            <div className="lg:col-span-2" data-id="element-3618" data-mp="1mwvsy9d85zuv">
              <div className="inline-flex items-center gap-2 px-2.5 py-1 border border-amber-400/40 bg-amber-400/10 mb-4" data-id="element-3619" data-mp="1mvkn8407yi14">
                <UserCircle className="w-3 h-3 text-amber-400" data-id="element-3620" data-mp="32gmmnrkx3vzg" />
                <span className="text-amber-400 text-[10px] tracking-[0.3em] uppercase font-bold" data-id="element-3621" data-mp="32fbhpofqki6r">
                  1:1 Pairing
                </span>
              </div>
              <h3 className="font-serif text-2xl sm:text-3xl text-white leading-tight mb-3" data-id="element-3622" data-mp="2sji7wctej2so">
                Paired with an Ivey MBA
              </h3>
              <p className="text-white/55 text-sm font-light leading-relaxed" data-id="element-3623" data-mp="3q81s3t7zqtex">
                Each fellow is individually matched with a current MBA student
                from the Ivey Business School as a personal mentor.
              </p>
            </div>
            <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-3624" data-mp="3bgjcvxmyh2wm">
              {['One-on-one weekly support', 'Career and project guidance', 'Network into Ivey alumni', 'Real conversations, not formal mentoring'].map(b => <div key={b} className="border border-amber-400/15 bg-black/40 p-4 flex items-start gap-3" data-id="element-3625" data-mp="22aj4hyoyimcr">
                  <CheckCircle2 className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" data-id="element-3626" data-mp="1shtwxewj1smp" />
                  <span className="text-white/75 text-sm font-light leading-snug" data-id="element-3627" data-mp="1xgodea70v53n">
                    {b}
                  </span>
                </div>)}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>;
// ====================================== LOCATION
const Location = () => <section className="relative py-24 md:py-32 bg-black border-t border-amber-400/10" data-id="element-3628" data-mp="19llns00e8xm4">
    <div className="container mx-auto px-4" data-id="element-3629" data-mp="1ejn9ytt49d1t">
      <SectionHeading eyebrow="Where It Happens" title="In Person." highlight="Or Virtual." intro="Selected students may participate in person at the Illuminarium in downtown Toronto&rsquo;s Distillery District — or virtually from anywhere — depending on availability, location, and cohort structure." data-id="element-3630" data-mp="33xs9hsqwywso" />
      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl mx-auto" data-id="element-3631" data-mp="sbhuva610dfb">
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} className="relative border border-amber-400/30 bg-gradient-to-br from-amber-950/15 via-black to-black p-7" data-id="element-3632" data-mp="16zkwk7d1d2yb">
          <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-3633" data-mp="1py6z7qo7b24c" />
          <div className="flex items-center gap-3 mb-4" data-id="element-3634" data-mp="1kyug9h0gaf71">
            <div className="w-11 h-11 border border-amber-400/50 bg-black/40 flex items-center justify-center" data-id="element-3635" data-mp="1kx9k5myhmo72">
              <MapPin className="w-5 h-5 text-amber-400" data-id="element-3636" data-mp="3mqgzuxi0j7th" />
            </div>
            <span className="text-amber-400 text-[10px] tracking-[0.3em] uppercase font-bold" data-id="element-3637" data-mp="3msjyu7158bac">
              In Person
            </span>
          </div>
          <h3 className="font-serif text-2xl text-white leading-tight mb-2" data-id="element-3638" data-mp="35vmmkpydjc8k">
            At the Illuminarium
          </h3>
          <p className="text-amber-400/85 text-[11px] tracking-[0.25em] uppercase font-bold mb-3" data-id="element-3639" data-mp="3anmr6vpe2tbh">
            Downtown Toronto · Distillery District
          </p>
          <p className="text-white/60 text-sm font-light leading-relaxed" data-id="element-3640" data-mp="2hyz2eq5fllt6">
            Selected students may participate on-site at the Illuminarium in
            Toronto&rsquo;s historic Distillery District — surrounded by the
            production, the creators, and the live cultural work.
          </p>
        </motion.div>
        <motion.div initial={{
        opacity: 0,
        x: 20
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} className="relative border border-amber-400/30 bg-gradient-to-br from-amber-950/15 via-black to-black p-7" data-id="element-3641" data-mp="2vz02w4hsar84">
          <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-3642" data-mp="1rnwxauqh1nsp" />
          <div className="flex items-center gap-3 mb-4" data-id="element-3643" data-mp="1mwf5oxac8zke">
            <div className="w-11 h-11 border border-amber-400/50 bg-black/40 flex items-center justify-center" data-id="element-3644" data-mp="1hyvgj2c3r2do">
              <Wifi className="w-5 h-5 text-amber-400" data-id="element-3645" data-mp="1d024lecgrjfm" />
            </div>
            <span className="text-amber-400 text-[10px] tracking-[0.3em] uppercase font-bold" data-id="element-3646" data-mp="180fomx593p34">
              Virtual
            </span>
          </div>
          <h3 className="font-serif text-2xl text-white leading-tight mb-3" data-id="element-3647" data-mp="13zngy8c11s8y">
            Anywhere in the World
          </h3>
          <p className="text-white/60 text-sm font-light leading-relaxed" data-id="element-3648" data-mp="z1ub5gemz097">
            Selected students outside the region or with scheduling needs may
            participate virtually with the same full access to mentors, tools,
            and project work.
          </p>
        </motion.div>
      </div>
      <p className="text-center mt-8 text-white/40 text-xs font-light italic max-w-2xl mx-auto" data-id="element-3649" data-mp="3mqg7lie66ku2">
        Format assignment is based on availability, location, and cohort
        structure.
      </p>
    </div>
  </section>;
// ====================================== COMMUNITY INITIATIVES
const CommunityInitiatives = () => {
  const items = [{
    icon: HandHeart,
    title: 'Community-Driven Innovation',
    body: 'Fellows contribute to projects with real impact in their communities.'
  }, {
    icon: Users,
    title: 'Real-World Collaboration',
    body: 'Work alongside other fellows, mentors, and partners on shared initiatives.'
  }, {
    icon: Rocket,
    title: 'Leadership Opportunities',
    body: 'Lead a workstream, run a session, or own a real piece of the production.'
  }, {
    icon: Sparkles,
    title: 'Meaningful Projects',
    body: 'Beyond workshops — fellows ship things that matter to people outside the cohort.'
  }];
  return <section className="relative py-24 md:py-32 bg-[#080808] border-t border-amber-400/10" data-id="element-3650" data-mp="zru8zljzia9n">
      <div className="container mx-auto px-4" data-id="element-3651" data-mp="zphlytzdd9pq">
        <SectionHeading eyebrow="Beyond the Sessions" title="Real Impact." highlight="Real Collaboration." intro="Beyond workshops and sessions, fellows participate in community initiatives, collaborative projects, and real-world AI innovation experiences." data-id="element-3652" data-mp="14jls5b0t9dpr" />
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto" data-id="element-3653" data-mp="gtrg2vobft6k">
          {items.map((it, i) => <motion.div key={it.title} initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.06
        }} data-id="element-3654" data-mp="luffo5qm1ice" data-mpi={i}>
              <PremiumCard icon={it.icon} title={it.title} body={it.body} data-id="element-3655" data-mp="3tb5scvgglg2o" />
            </motion.div>)}
        </div>
      </div>
    </section>;
};
// ====================================== APPLY WITH A FRIEND
const ApplyWithFriend = () => <section className="relative py-20 md:py-24 bg-black border-t border-amber-400/10" data-id="element-3656" data-mp="711vamkabgqn">
    <div className="container mx-auto px-4" data-id="element-3657" data-mp="3fj7s7a4q6uin">
      <div className="max-w-5xl mx-auto" data-id="element-3658" data-mp="3ak4ukar2rqkp">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="relative border border-amber-400/30 bg-gradient-to-br from-amber-950/15 via-black to-black p-8 sm:p-10 lg:p-12 text-center" data-id="element-3659" data-mp="3alg0ag42z8eg">
          <span className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-3660" data-mp="2rslx0jftl4q2" />
          <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-amber-400/40 bg-amber-400/10 mb-6" data-id="element-3661" data-mp="2hndl6e4tznvg">
            <UserPlus className="w-3.5 h-3.5 text-amber-400" data-id="element-3662" data-mp="27ramji96u7d9" />
            <span className="text-amber-400 tracking-[0.3em] text-[10px] uppercase font-bold" data-id="element-3663" data-mp="27t3s0u82eljo">
              Apply With a Friend
            </span>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl text-white mb-4 leading-tight" data-id="element-3664" data-mp="30fok5wwd71oa">
            The best work happens{' '}
            <span className="text-amber-400 italic" data-id="element-3665" data-mp="35ej0ms6v0e58">in pairs.</span>
          </h2>
          <p className="text-white/65 text-base sm:text-lg font-light leading-relaxed max-w-2xl mx-auto mb-6" data-id="element-3666" data-mp="2qgqoma0iu7vs">
            Students are encouraged to apply with a friend or partner. Some
            activities and projects are collaborative — teamwork, creativity,
            and shared learning are part of the design.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 max-w-2xl mx-auto" data-id="element-3667" data-mp="1jzln7hxy8imt">
            {['Work in pairs', 'Learn together', 'Build accountability', 'Ship as a team'].map(b => <div key={b} className="border border-amber-400/20 bg-black/40 px-3 py-2 text-[11px] tracking-wide text-white/70 font-light" data-id="element-3668" data-mp="1qk2ttvauu44e">
                {b}
              </div>)}
          </div>
        </motion.div>
      </div>
    </div>
  </section>;
// ====================================== COST & COMMITMENT (FREE + REFUNDABLE DEPOSIT)
const CostCommitment = () => <section className="relative py-24 md:py-32 bg-[#080808] border-t border-amber-400/10" data-id="element-3669" data-mp="297ocjd6kekky">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(212,175,55,0.06),_transparent_60%)] pointer-events-none" data-id="element-3670" data-mp="249wmkophd8je" />
    <div className="container mx-auto px-4 relative z-10" data-id="element-3671" data-mp="sv98pw9omlxo">
      <div className="max-w-5xl mx-auto" data-id="element-3672" data-mp="stptm8zrigxh">
        <div className="text-center mb-12" data-id="element-3673" data-mp="34iw9aorso53i">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-amber-400 bg-amber-400/10 mb-6 shadow-[0_0_25px_rgba(212,175,55,0.15)]" data-id="element-3674" data-mp="34jxisot5wjj4">
            <Gift className="w-3.5 h-3.5 text-amber-400" data-id="element-3675" data-mp="3eh28gym7ux4o" />
            <span className="text-amber-400 tracking-[0.3em] text-[10px] uppercase font-bold" data-id="element-3676" data-mp="3efhgngradsai">
              Fully Sponsored for Selected Students
            </span>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-white mb-5 leading-[1.05] tracking-tight" data-id="element-3677" data-mp="3o5txoyzw73bw">
            The Opportunity is <span className="text-amber-400" data-id="element-3678" data-mp="3sa1qp8ddv5fx">Free.</span>
          </h2>
          <div className="w-16 h-px bg-amber-400/40 mx-auto mb-6" data-id="element-3679" data-mp="12fe2q8orsmx" />
          <p className="text-white/70 text-base sm:text-lg font-light leading-relaxed max-w-3xl mx-auto" data-id="element-3680" data-mp="618uaqfcxxqs">
            The fellowship itself is fully sponsored for selected students. To
            ensure commitment and accountability, accepted students may be
            required to place a refundable{' '}
            <span className="text-amber-400 font-medium" data-id="element-3681" data-mp="1zspbne294mvd">USD $250 deposit</span>
            , returned in full upon successful completion of the program.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-id="element-3682" data-mp="29qdb2hasyijv">
          {/* Program is Free */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="relative border-2 border-amber-400/50 bg-gradient-to-br from-amber-950/20 via-black to-black p-8 shadow-[0_0_40px_rgba(212,175,55,0.15)]" data-id="element-3683" data-mp="24q7kz5k6l43w">
            <span className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-3684" data-mp="2nhs4zj69045p" />
            <div className="flex items-center gap-3 mb-5" data-id="element-3685" data-mp="32c4y0nfskh8g">
              <div className="w-12 h-12 border border-amber-400 bg-amber-400/10 flex items-center justify-center" data-id="element-3686" data-mp="2xgpm411cgveo">
                <Gift className="w-5 h-5 text-amber-400" data-id="element-3687" data-mp="3h1zm83erfua1" />
              </div>
              <div data-id="element-3688" data-mp="3l1i9h5u20hg1">
                <p className="text-amber-400 text-[10px] tracking-[0.3em] uppercase font-bold" data-id="element-3689" data-mp="3l20cclb81twx">
                  Program Cost
                </p>
                <p className="font-serif text-3xl sm:text-4xl text-amber-400 leading-none mt-1" data-id="element-3690" data-mp="3c1lp3gep64qs">
                  Free
                </p>
              </div>
            </div>
            <p className="text-white/70 text-sm font-light leading-relaxed mb-4" data-id="element-3691" data-mp="323xi9s3th6s9">
              Fully sponsored for selected students. No tuition, no hidden fees,
              no obligations beyond commitment.
            </p>
            <ul className="space-y-2 pt-4 border-t border-amber-400/15" data-id="element-3692" data-mp="2tb3x5e1ovd6h">
              {['Mentorship from Emmy & Oscar winners', 'Faculty access (Ivey & INSEAD)', 'AI tooling and project work', 'Letter of recommendation from Prof. Ash Singh'].map(b => <li key={b} className="flex items-start gap-2.5 text-white/65 text-sm font-light" data-id="element-3693" data-mp="2yuxo2hi64cwo">
                  <CheckCircle2 className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" data-id="element-3694" data-mp="2pu9hu8z0nguc" />
                  <span data-id="element-3695" data-mp="2kvy7zy8q16c4">{b}</span>
                </li>)}
            </ul>
          </motion.div>

          {/* Refundable Deposit */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="relative border border-amber-400/30 bg-gradient-to-br from-amber-950/12 via-black to-black p-8" data-id="element-3696" data-mp="2fy65ybed43u7">
            <span className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-amber-400/70 to-transparent" data-id="element-3697" data-mp="1s6ilocblvr0w" />
            <div className="flex items-center gap-3 mb-5" data-id="element-3698" data-mp="1i2alfilmvtbz">
              <div className="w-12 h-12 border border-amber-400/50 bg-black/40 flex items-center justify-center" data-id="element-3699" data-mp="1n1wz1t5djblb">
                <RefreshCw className="w-5 h-5 text-amber-400" data-id="element-3700" data-mp="1s103bbdj5ds8" />
              </div>
              <div data-id="element-3701" data-mp="1x02y82zsq3mh">
                <p className="text-amber-400 text-[10px] tracking-[0.3em] uppercase font-bold" data-id="element-3702" data-mp="1x0mezgdpmlyx">
                  Refundable Commitment
                </p>
                <p className="font-serif text-3xl sm:text-4xl text-white leading-none mt-1" data-id="element-3703" data-mp="zarjlf3cilbj">
                  <span className="text-amber-400/80 text-2xl align-top" data-id="element-3704" data-mp="149cklgqf9911">
                    USD
                  </span>{' '}
                  $250
                </p>
              </div>
            </div>
            <p className="text-white/70 text-sm font-light leading-relaxed mb-4" data-id="element-3705" data-mp="1e3bg3wvzpuk0">
              Accepted students may be required to place a refundable deposit.{' '}
              <span className="text-amber-400" data-id="element-3706" data-mp="ljmrqz67yxbr">Returned in full</span> upon
              successful completion.
            </p>
            <ul className="space-y-2 pt-4 border-t border-amber-400/15" data-id="element-3707" data-mp="3pybpxa89vdcn">
              {['Ensures commitment and accountability', 'Fully refunded on successful completion', 'Requires attending all required sessions', 'Requires active participation'].map(b => <li key={b} className="flex items-start gap-2.5 text-white/65 text-sm font-light" data-id="element-3708" data-mp="378n88itfltf8">
                  <DollarSign className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" data-id="element-3709" data-mp="3c280du8cft6i" />
                  <span data-id="element-3710" data-mp="2x9e7n7429gjw">{b}</span>
                </li>)}
            </ul>
          </motion.div>
        </div>

        {/* Why */}
        <div className="mt-8 border border-amber-400/20 bg-black/40 p-5 sm:p-6" data-id="element-3711" data-mp="2oaaoy48t335x">
          <div className="flex items-start gap-4" data-id="element-3712" data-mp="2sc5yfw0w4mus">
            <Info className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" data-id="element-3713" data-mp="2sbw7a54409ni" />
            <div data-id="element-3714" data-mp="29nbz2dplkgtb">
              <p className="text-amber-400 tracking-[0.25em] text-[10px] uppercase font-bold mb-2" data-id="element-3715" data-mp="29owu04ftq1rp">
                Why the Deposit?
              </p>
              <p className="text-white/65 text-sm font-light leading-relaxed" data-id="element-3716" data-mp="2ios92kci2gx0">
                The deposit exists to ensure that selected students take the
                opportunity seriously and show up fully. Students who complete
                the program receive the full $250 back. The fellowship itself
                remains free.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>;
// ====================================== APPLY (Typeform)
const Apply = () => <section id="apply" className="relative py-24 md:py-32 bg-black border-t border-amber-400/10 scroll-mt-20" data-id="element-3717" data-mp="2xd6bwv9x25dk">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.08),_transparent_70%)] pointer-events-none" data-id="element-3718" data-mp="3be8xz80wvfoh" />
    <div className="container mx-auto px-4 relative z-10" data-id="element-3719" data-mp="36cjjfiehcjvn">
      <SectionHeading eyebrow="Apply for the Fellowship" title="Begin Your" highlight="Application" intro="Only a small percentage of applicants are accepted. Please take the application seriously — thoughtful responses matter." data-id="element-3720" data-mp="2429iedmbqbjw" />

      <motion.div initial={{
      opacity: 0,
      y: 16
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} className="max-w-3xl mx-auto mt-12 mb-8 border border-amber-400/25 bg-amber-950/8 p-5 sm:p-6" data-id="element-3721" data-mp="2hswk8endshk7">
        <p className="text-amber-400 text-[10px] tracking-[0.3em] uppercase font-bold mb-3" data-id="element-3722" data-mp="2vt77l7phfe0m">
          What We&rsquo;ll Ask
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2.5 text-sm font-light text-white/70" data-id="element-3723" data-mp="30p4phige8sfi">
          {['Your contact details', 'A bit about yourself', 'Resume or LinkedIn profile', 'Why you want to join', 'Expectations & commitment', 'Whether you\u2019re applying with a friend'].map(q => <div key={q} className="flex items-center gap-2.5" data-id="element-3724" data-mp="u0vr7d1jru9x">
              <ChevronRight className="w-3 h-3 text-amber-400 shrink-0" data-id="element-3725" data-mp="p4nfyaqh0l81" />
              <span data-id="element-3726" data-mp="k5arjljyb63c">{q}</span>
            </div>)}
        </div>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.8
    }} className="max-w-4xl mx-auto relative border border-amber-400/30 bg-black p-1.5 shadow-[0_0_60px_rgba(212,175,55,0.15)]" data-id="element-3727" data-mp="1hsayzlc3f3sk">
        <span className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-3728" data-mp="3sl409e81jd9u" />
        <iframe src="https://form.typeform.com/to/bynEjrbu" title="Entrepreneurship and AI Fellowship Application" className="w-full h-[720px] bg-white" frameBorder="0" allow="camera; microphone; autoplay; encrypted-media; fullscreen" data-id="element-3729" data-mp="3oi7p28ktyjfx" />
      </motion.div>

      <p className="text-center mt-6 text-white/40 text-xs font-light italic flex items-center justify-center gap-2" data-id="element-3730" data-mp="16dy44x8q79t0">
        <Heart className="w-3 h-3 text-amber-400" data-id="element-3731" data-mp="1b8vb7ymg3939" />
        Applications reviewed on a rolling basis · Limited spots
      </p>
    </div>
  </section>;
// ====================================== FINAL CTA
const FinalCTA = () => <section className="relative py-24 md:py-32 bg-black border-t border-amber-400/10 overflow-hidden" data-id="element-3732" data-mp="1q0dwoby2pvu0">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.1),_transparent_60%)] pointer-events-none" data-id="element-3733" data-mp="1u1691e28sgui" />
    <div className="container mx-auto px-4 relative z-10" data-id="element-3734" data-mp="ejdwku5hicpf">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} className="max-w-3xl mx-auto text-center" data-id="element-3735" data-mp="in25bg5urec9">
        <Eyebrow data-id="element-3736" data-mp="66au0ghqy0op">For Students &amp; Parents</Eyebrow>
        <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl text-white mt-5 mb-6 leading-[1.05] tracking-tight" data-id="element-3737" data-mp="l05w93necrqx">
          A summer that{' '}
          <span className="text-amber-400 italic" data-id="element-3738" data-mp="g09r312yqu9s">actually matters.</span>
        </h2>
        <div className="w-20 h-px bg-amber-400/40 mx-auto mb-8" data-id="element-3739" data-mp="3oftma5n72mrh" />
        <p className="text-base sm:text-lg text-white/65 font-light leading-relaxed mb-4" data-id="element-3740" data-mp="3jk5tlg9i7bh8">
          For ambitious high school and university students ready to build with
          AI, learn from world-class faculty, and earn something real.
        </p>
        <p className="text-sm text-white/45 font-light leading-relaxed mb-10" data-id="element-3741" data-mp="35rg4v42lbsgf">
          Just 3 hours a week. Free for selected students. Refundable $250
          commitment. Limited spots.
        </p>
        <a href="#apply" className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-amber-400 text-black text-xs tracking-[0.3em] uppercase font-bold hover:bg-amber-300 transition-colors shadow-[0_0_50px_rgba(212,175,55,0.4)]" data-id="element-3742" data-mp="3qgqtsdgisfzj">
          Begin the Application
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" data-id="element-3743" data-mp="8314hdgq4lnj" />
        </a>
        <p className="mt-6 text-white/40 text-xs font-light italic" data-id="element-3744" data-mp="37i4riv99oiu1">
          Parents are welcome to submit on behalf of their child.
        </p>
        <p className="mt-3 text-amber-400/70 text-xs font-light tracking-[0.25em] uppercase" data-id="element-3745" data-mp="3bji0vfbkj45p">
          Presented by Akal Steel
        </p>
        <p className="mt-1 text-white/35 text-[11px] font-light tracking-wide" data-id="element-3746" data-mp="3lh4iwao5zbtw">
          In partnership with the Sikh Foundation of Canada
        </p>
      </motion.div>
    </div>
  </section>;
// ====================================== PAGE
export const AIEntrepreneurshipPage = () => <LanguageProvider data-id="element-3747" data-mp="2l97tb09rndy5">
    <div className="bg-black min-h-screen text-white selection:bg-amber-400/30" data-id="element-3748" data-mp="2l9r0blxvohxe">
      <ScrollProgress data-id="element-3749" data-mp="2ga4kd4qo0nkw" />
      <Navbar data-id="element-3750" data-mp="2g9uuri9j8ifq" />
      <main data-id="element-3751" data-mp="2gb61nqy9y6b2">
        <TrailerIntro data-id="element-3752" data-mp="2gamt30uiku9p" />
        <Hero data-id="element-3753" data-mp="2gc7jqfdpjt2a" />
        <AtAGlance data-id="element-3754" data-mp="2bjeg5utz6qrs" />
        <WhyThisMatters data-id="element-3755" data-mp="2biwa66higy6o" />
        <ThePeople data-id="element-3756" data-mp="2bi3d5x8ctn1q" />
        <SelectivityBlock data-id="element-3757" data-mp="2bhlbum6u4imy" />
        <WhatYoullWorkOn data-id="element-3758" data-mp="26kbac8668gir" />
        <BuildTeams data-id="element-3759" data-mp="26l38nqr5kscq" />
        <AITools data-id="element-3760" data-mp="26jjrzz1l4faf" />
        <Location data-id="element-3761" data-mp="26k1maq4osjfp" />
        <CommunityInitiatives data-id="element-3762" data-mp="26igyrkgsi0rc" />
        <ApplyWithFriend data-id="element-3763" data-mp="22izuuvh0hboa" />
        <CostCommitment data-id="element-3764" data-mp="22jhqpqzrhnvo" />
        <Apply data-id="element-3765" data-mp="22gn4btx6tszm" />
        <FinalCTA data-id="element-3766" data-mp="1xjmwbdgs2g5v" />
      </main>
      <LandingFooter data-id="element-3767" data-mp="1xjdfafdpnjcb" />
    </div>
  </LanguageProvider>;
// ====================================== LANDING FOOTER (minimal — no nav distractions)
const LandingFooter = () => <footer className="relative bg-black border-t border-amber-400/20 py-12" data-id="element-3768" data-mp="1nlp9muekgrfd">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_rgba(212,175,55,0.06),_transparent_70%)] pointer-events-none" data-id="element-3769" data-mp="1nnjt04a6wbhc" />
    <div className="container mx-auto px-4 relative z-10" data-id="element-3770" data-mp="3hyykapiumiz7">
      <div className="max-w-3xl mx-auto text-center" data-id="element-3771" data-mp="3hxnjmykp9rcb">
        <p className="font-serif text-amber-400 text-base sm:text-lg tracking-[0.3em] uppercase mb-3" data-id="element-3772" data-mp="2qv5qe08bz4y4">
          Sikh Kingdoms
        </p>
        <p className="text-white/45 text-xs tracking-[0.25em] uppercase mb-6 font-light" data-id="element-3773" data-mp="2vvbjlku90zib">
          Entrepreneurship and AI Opportunity · Summer Fellowship
        </p>
        <div className="w-12 h-px bg-amber-400/30 mx-auto mb-6" data-id="element-3774" data-mp="2m234me9sozsh" />
        <p className="text-white/55 text-xs sm:text-sm font-light leading-relaxed mb-6 max-w-xl mx-auto" data-id="element-3775" data-mp="2833hxc5ueuyy">
          Application only. Free for selected students. Refundable $250
          commitment. Just 3 hours a week.
        </p>
        <a href="#apply" className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-amber-400/45 hover:border-amber-400 hover:bg-amber-400/5 text-amber-400 text-[10px] tracking-[0.3em] uppercase font-bold transition-colors mb-8" data-id="element-3776" data-mp="1z49mhp490amz">
          Begin Application
          <ArrowRight className="w-3 h-3" data-id="element-3777" data-mp="1cn955zmsmwi" />
        </a>
        <p className="text-amber-400/70 text-[10px] tracking-[0.3em] uppercase font-bold mb-1" data-id="element-3778" data-mp="1lupn7sv8wbxk">
          Presented by Akal Steel
        </p>
        <p className="text-white/30 text-[11px] font-light tracking-wide mb-1" data-id="element-3779" data-mp="1hv74n3qwccxw">
          In partnership with the Sikh Foundation of Canada
        </p>
        <p className="text-white/25 text-[10px] font-light italic" data-id="element-3780" data-mp="1qukbrprjspfg">
          For questions, please apply and we&rsquo;ll be in touch.
        </p>
      </div>
    </div>
  </footer>;